import React, { useEffect, useState } from 'react'
import { EmissionWrapper, TabEl } from './style';
import { EmissionCard } from './EmissionCard';
import DairyCowImage from '@assets/svgs/klima/Icon_Milchkuh.svg'
import HeiferImage from '@assets/svgs/klima/Icon_Farse.svg'
import GrassSilageImage from '@assets/svgs/klima/Icon_Gras.svg'
import CornSilageImage from '@assets/svgs/klima/Icon_Mais.svg'
import { useTranslation } from 'react-i18next';
import { TabKey } from './types';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { mainColumnHeaders, heiferTableColumnHeaders, getSilageColumnTypes } from './constants';
import InfoPopup from '~/components/InfoPopup';
import { Co2ResultsType } from '~/api/services/co2';
import { formatNumber } from '~/utils/helpers';

const COLORS = ['#4A90E2', '#FF9800', '#4CAF50', '#FFC107', '#9C27B0', '#F44336', '#795548', '#607D8B'];

interface Props {
  results: Co2ResultsType
}

const ResultsCo2: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<TabKey>('cows');

  const [results, setResults] = useState(props.results)

  useEffect(() => {
    setResults(props.results)
  }, [props.results])

  const getColumnHeaders = () => {
    switch (activeTab) {
      case 'cows':
        return mainColumnHeaders;
      case 'youngCattle':
        return heiferTableColumnHeaders;
      case 'grassSilage':
        return getSilageColumnTypes('grassSilage');
      case 'maizeSilage':
        return getSilageColumnTypes('maizeSilage');
      default:
        return mainColumnHeaders;
    }
  };

  const getPieChartData = () => {
    const data = results[activeTab];
    const headers = getColumnHeaders().filter(header => header.pieValue);
    const pieData = [];

    headers.forEach(header => {
      const value = data[header.key];
      if (typeof value === 'number' && value > 0) {
        pieData.push({
          name: header.name,
          value: value,
          key: header.key
        });
      }
    });

    return pieData
  };

  if (!results) {
    return <></>
  }

  function getTotalKey() {
    switch (activeTab) {
      case 'cows':
        return 'total'
      case 'grassSilage':
        return 'grassSilageGhgEmissionPerHa'
      case 'maizeSilage':
        return 'maizeSilageGhgEmissionPerHa'
      case 'youngCattle':
        return 'youngCattleTotal'
    }
  }

  function getResultsTitle() {
    switch (activeTab) {
      case 'cows':
        return 'THG Emissionen (in kg CO2-Äq./Kuh u. Jahr)'
      case 'grassSilage':
        return 'THG-Emissionen Verteilung'
      case 'maizeSilage':
        return 'THG-Emissionen Verteilung'
      case 'youngCattle':
        return 'Verteilung der THG Emissionen (kg CO₂-Äq./je erz. Färse)'
    }
  }

  const hasMaizeSilage = results.maizeSilage && Object.keys(results.maizeSilage).length > 0
  const hasGrassSilage = results.grassSilage && Object.keys(results.grassSilage).length > 0
  const hasYoungCattle = results.youngCattle && Object.keys(results.youngCattle).length > 0

  function renderLegend() {
    const totalKey = getTotalKey()
    const total = results[activeTab][totalKey]
    const pieData = getPieChartData();

    return getColumnHeaders()
      .filter((header) => header.pieValue && !header.total)
      .map((header) => {
        const value = results[activeTab][header.key];
        const percentage = (value / total * 100);
        return {
          header,
          value,
          percentage,
          dataIndex: pieData.findIndex(item => item.key === header.key)
        };
      })
      .sort((a, b) => b.percentage - a.percentage)
      .map(({ header, value, percentage, dataIndex }) => {
        const headerWithInfo = header as any;
        const color = dataIndex !== -1 ? COLORS[dataIndex % COLORS.length] : '#000';
        return (
          <TabEl.SmallCard key={header.key}>
            <TabEl.SmallCardLabel>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: color,
                    borderRadius: '2px'
                  }}
                />
                {header.name}
              </div>
              {headerWithInfo.info && (
                <InfoPopup
                  content={headerWithInfo.info}
                  position="right center"
                  styleIcon={{ fontSize: '1.3em' }}
                  miWidth={400}
                />
              )}
            </TabEl.SmallCardLabel>
            <TabEl.SmallCardValue>
              {`${formatNumber(value, 0)} [${formatNumber(percentage, 2)}%]`}
            </TabEl.SmallCardValue>
          </TabEl.SmallCard>
        );
      })
  }

  return <div>
    <EmissionWrapper>
      <EmissionCard
        title={t('GHG emissions per cow per year')}
        value={results.cows.total}
        unit='kg CO<sub>2</sub>-Äq./Kuh u. Jahr'
        description='Dieser Wert entspricht der Summe aller berücksichtigten Treibhausgasemissionen.'
        icon={<img src={DairyCowImage} style={{ width: 28, borderRadius: '50%' }} />}
        variant='cows'
        isActive={activeTab === 'cows'}
        onClick={() => setActiveTab('cows')}
        additionalDetail={{
          value: results.cows.ghgEmissionMilkIdf,
          unit: 'kg CO<sub>2</sub>-Äq./kg Milch (FPCM)',
          label: ''
        }}
      />
      {hasYoungCattle && <EmissionCard
        title='THG-Emissionen gesamt Färsenaufzucht'
        value={results.youngCattle.youngCattleTotal}
        unit='kg CO<sub>2</sub>-Äq./erz. Färse'
        description='Gesamtemissionen der Färsenaufzucht einschließlich Fütterung, Haltung, Wasserversorgung und Energieverbrauch nach IDF-Standard vom Kalb bis zur tragenden Färse.'
        icon={<img src={HeiferImage} style={{ width: 28, borderRadius: '50%' }} />}
        variant='youngCattle'
        isActive={activeTab === 'youngCattle'}
        onClick={() => setActiveTab('youngCattle')}
      />}
      {hasGrassSilage && <EmissionCard
        title='Grassilage-THG-Emissionen je Hektar'
        value={results.grassSilage.grassSilageGhgEmissionPerHa}
        unit='kg CO<sub>2</sub>-Äq./ha'
        description='Treibhausgasemissionen der Grassilage-Produktion pro Hektar einschließlich Saatgut, Düngemittel, Pflanzenschutz, Siloanlage und Dieselverbrauch nach internationalen Standards.'
        icon={<img src={GrassSilageImage} style={{ width: 28, borderRadius: '50%' }} />}
        variant='grassSilage'
        isActive={activeTab === 'grassSilage'}
        onClick={() => setActiveTab('grassSilage')}
      />}
      {hasMaizeSilage && <EmissionCard
        title='Maissilage-THG-Emissionen je Hektar'
        value={results.maizeSilage.maizeSilageGhgEmissionPerHa}
        unit='kg CO<sub>2</sub>-Äq./ha'
        description='Gesamtemissionen der Maissilage-Produktion pro Hektar mit Saatgut, Düngung, Pflanzenschutz, Maschineneinsatz und Siloanlage nach anerkannten Berechnungsmethoden.'
        icon={<img src={CornSilageImage} style={{ width: 28, borderRadius: '50%' }} />}
        variant='maizeSilage'
        isActive={activeTab === 'maizeSilage'}
        onClick={() => setActiveTab('maizeSilage')}
      />}
    </EmissionWrapper>


    <TabEl.ContentWrapper>

      <TabEl.Segment>
        <TabEl.SegmentTitle>{'Ergebnisübersicht'}</TabEl.SegmentTitle>
        {getColumnHeaders()
          .filter((header) => !header.pieValue && !header.total)
          .map((header) => {
            const value = results[activeTab][header.key];
            const headerWithInfo = header as any;
            //@ts-ignore
            return <TabEl.SmallCard key={header.key} $border={header.border}>
              <TabEl.SmallCardLabel>
                {header.name}
                {headerWithInfo.info && (
                  <InfoPopup
                    content={headerWithInfo.info}
                    position="right center"
                    styleIcon={{ fontSize: '1.3em' }}
                    miWidth={400}
                  />
                )}
              </TabEl.SmallCardLabel>
              <TabEl.SmallCardValue>
                {/*@ts-ignore*/}
                {formatNumber(value, header?.decimal || 0)}
                {header.unit && (
                  <div
                    dangerouslySetInnerHTML={{ __html: header.unit }}
                    style={{ fontSize: '12px' }}
                  />
                )}
              </TabEl.SmallCardValue>
            </TabEl.SmallCard>;
          })}
        {activeTab === 'youngCattle' && renderLegend()}
      </TabEl.Segment>

      <TabEl.Segment>
        <TabEl.SegmentTitle>{getResultsTitle()}</TabEl.SegmentTitle>
        <TabEl.ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={getPieChartData()}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={activeTab !== 'cows' ? ({ name, percent }) => `${name} ${(percent * 100).toFixed(2)}%` : false}
                outerRadius={150}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
              >
                {getPieChartData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [
                  value.toLocaleString(),
                  name
                ]}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </TabEl.ChartContainer>
        {activeTab !== 'youngCattle' && renderLegend()}
      </TabEl.Segment>
    </TabEl.ContentWrapper>
  </div>
}
export default ResultsCo2